/**
 * ImmersiveWorkoutVideoBackground - Fullscreen video/image background
 */
import * as React from 'react';
import { useState, useEffect, useMemo } from 'react';
import { observer } from 'mobx-react';
import styled from '@emotion/styled';
import { ViewSet } from '../../../../Model/WorkoutLog/ViewSet';
import { useRootStore } from '../../../../Store/useRootStore';
import { getMediaLargeOrMediumOrSmallest } from '../../../../Model/Media/Media';

const Container = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: #111;
  cursor: pointer;
`;

const MediaContainer = styled.div`
  width: 100%;
  height: 100%;
  overflow: hidden;
`;

const Video = styled.video`
  width: 100%;
  height: 100%;
  object-fit: cover;
`;

const Image = styled.div<{ src?: string }>`
  width: 100%;
  height: 100%;
  background-image: ${({ src }) => (src ? `url(${src})` : 'none')};
  background-size: cover;
  background-position: center;
  background-color: #1a1a1a;
`;

const Placeholder = styled.div`
  width: 100%;
  height: 100%;
  background: #1a1a1a;
`;

export type ImmersiveWorkoutVideoBackgroundProps = {
  viewSet?: ViewSet;
  onPress?: () => void;
  allowSound?: boolean;
};

export const ImmersiveWorkoutVideoBackground: React.FC<ImmersiveWorkoutVideoBackgroundProps> = observer(
  ({ viewSet, onPress, allowSound = false }) => {
    const { remoteSettings } = useRootStore();
    const videoRef = React.useRef<HTMLVideoElement>(null);

    const viewBlock = viewSet?.viewBlock;
    const exerciseId = viewBlock?.exercise?.id;
    const videoMedia = viewBlock?.previewVideoMedia ?? viewBlock?.videoMedia;
    const imageMedia = viewBlock?.exerciseMedia;
    const videoAutoplay = remoteSettings.get('exercise_video_autoplay') ?? true;
    const hasVideo = !!videoMedia;

    const [showVideo, setShowVideo] = useState(hasVideo && videoAutoplay);

    // Reset when exercise changes
    useEffect(() => {
      setShowVideo(hasVideo && videoAutoplay);
    }, [exerciseId, hasVideo, videoAutoplay]);

    const videoSource = useMemo(() => {
      if (!videoMedia) return undefined;
      return getMediaLargeOrMediumOrSmallest(videoMedia);
    }, [videoMedia]);

    const imageUri = imageMedia ? getMediaLargeOrMediumOrSmallest(imageMedia) : undefined;

    useEffect(() => {
      const video = videoRef.current;
      if (!video || !showVideo || !videoSource) return;
      video.muted = !allowSound;
      const playPromise = video.play();
      if (playPromise && typeof playPromise.catch === 'function') {
        playPromise.catch((error) => console.warn('[ImmersiveWorkoutVideoBackground] Video playback failed:', error));
      }
    }, [allowSound, showVideo, videoSource, exerciseId]);

    return (
      <Container onClick={onPress}>
        <MediaContainer>
          {showVideo && hasVideo && videoSource ? (
            <Video ref={videoRef} src={videoSource} autoPlay loop muted={!allowSound} playsInline preload="auto" />
          ) : imageUri ? (
            <Image src={imageUri} />
          ) : (
            <Placeholder />
          )}
        </MediaContainer>
      </Container>
    );
  },
);
